import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { pca } from '@/lib/msal';
export const useAuthStore = create()(persist((set) => ({
    devUser: null,
    me: null,
    impersonateUserId: null,
    setDevUser: (email) => set({ devUser: email }),
    setMe: (me) => set({ me }),
    setImpersonateUserId: (userId) => set({ impersonateUserId: userId }),
    // Local clear is synchronous; the MSAL redirect (if active) navigates
    // away from the SPA, so the order matters: clear store first so any
    // pre-redirect rerender shows a logged-out UI, then kick off MSAL's
    // logout which lands the user back at /login.
    logout: () => {
        set({ devUser: null, me: null, impersonateUserId: null });
        if (pca) {
            // Fire-and-forget — pca.logoutRedirect navigates the page.
            pca.logoutRedirect({
                account: pca.getActiveAccount() ?? undefined,
                postLogoutRedirectUri: `${window.location.origin}/login`,
            }).catch((err) => console.error('[auth] msal logoutRedirect failed:', err));
        }
    },
}), { name: 'itsm-auth' }));
export function hasPerm(me, perm) {
    if (!me)
        return false;
    if (me.permissions.includes('platform.admin'))
        return true;
    return me.permissions.includes(perm);
}
export function hasRole(me, name) {
    if (!me)
        return false;
    return me.roles.some((r) => r.name === name);
}
// Mirrors `backend/app/core/rbac.py::_ROLE_NAME_TO_LEVEL`. Legacy names
// ("Service Desk Engineer", "Engineer") kept as a one-release safety net
// for any session whose persisted `/me` data still carries the pre-rename
// names. After migration 0049 + the next /me refresh, only the new names
// will appear in `me.roles[]`. Remove the legacy aliases after one
// release cycle.
const ROLE_NAME_TO_LEVEL = {
    'Super Admin': 'ADMIN',
    'Tenant Admin': 'ADMIN',
    'Service Desk Manager': 'MANAGER',
    'Service Desk Agent': 'AGENT',
    'Field Engineer': 'AGENT',
    // ----- legacy aliases (post-rename safety net) -----
    'Service Desk Engineer': 'AGENT',
    'Engineer': 'AGENT',
};
const LEVEL_RANK = {
    ADMIN: 30,
    MANAGER: 20,
    AGENT: 10,
};
/** Returns the principal's *highest* coarse role level, or null if they
 *  hold no internal Service Desk role (e.g. Customer User, Vendor). */
export function effectiveRole(me) {
    if (!me)
        return null;
    if (me.permissions.includes('platform.admin'))
        return 'ADMIN';
    let best = null;
    let bestRank = 0;
    for (const r of me.roles) {
        const lvl = ROLE_NAME_TO_LEVEL[r.name];
        if (lvl && LEVEL_RANK[lvl] > bestRank) {
            best = lvl;
            bestRank = LEVEL_RANK[lvl];
        }
    }
    return best;
}
/** True iff the principal's role is in the allowed list. */
export function isRoleAllowed(me, allowed) {
    const role = effectiveRole(me);
    return role !== null && allowed.includes(role);
}
export function resolvePersona(me) {
    if (!me)
        return 'customer';
    if (me.permissions.includes('platform.admin'))
        return 'super_admin';
    if (hasRole(me, 'Vendor'))
        return 'vendor';
    if (hasRole(me, 'Customer User'))
        return 'customer';
    if (hasRole(me, 'Tenant Admin') ||
        hasRole(me, 'Service Desk Manager') ||
        hasRole(me, 'Read-Only')) {
        return 'admin';
    }
    return 'agent';
}
export function defaultLandingFor(persona) {
    switch (persona) {
        case 'vendor': return '/vendor';
        case 'customer': return '/portal';
        default: return '/';
    }
}
